/**
 * Tiny fetch wrapper for /api/* calls.
 *
 * Sends:
 *   - Content-Type: application/json (on POST/PATCH/PUT bodies)
 *   - X-Client-Time: <ISO-8601 UTC> (on every credential endpoint per D-39)
 *   - credentials: "include" (so the pm_session cookie travels)
 *
 * Returns parsed JSON on success; throws ApiError(status, code, params) on
 * 4xx/5xx so React Query mutations/queries can surface the {code} envelope.
 *
 * CP4 compliance: does NOT log request bodies or response bodies; only the
 * HTTP status code on error.
 */

export class ApiError extends Error {
  readonly params: Record<string, unknown>;

  constructor(
    public readonly status: number,
    public readonly code: string,
    params: Record<string, unknown> = {},
  ) {
    super(`ApiError ${status} ${code}`);
    this.params = params;
  }
}

const CREDENTIAL_PATHS = new Set<string>([
  "/api/auth/pm/login",
  "/api/auth/pm/logout",
  "/api/auth/pm/forgot-password",
  "/api/auth/pm/change-password",
  "/api/auth/pm/change-security-q",
  "/api/auth/pm/change-security-a",
  "/api/worker/auth/login",
  "/api/admin/workers",
]);

function needsClientTime(path: string): boolean {
  return (
    CREDENTIAL_PATHS.has(path) ||
    path.startsWith("/api/auth/pm/") ||
    path === "/api/worker/auth/login"
  );
}

async function _request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (needsClientTime(path)) {
    headers["X-Client-Time"] = new Date().toISOString();
  }

  const response = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "include",
  });

  if (response.status === 204) return undefined as T;

  let payload: { code?: string; params?: Record<string, unknown> } = {};
  try {
    payload = await response.json();
  } catch {
    // Non-JSON response — fall through with empty payload.
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload.code ?? "ERR_GENERIC",
      payload.params ?? {},
    );
  }

  return payload as T;
}

export const apiGet = <T>(path: string): Promise<T> => _request<T>("GET", path);
export const apiPost = <T>(path: string, body?: unknown): Promise<T> =>
  _request<T>("POST", path, body);
export const apiPatch = <T>(path: string, body?: unknown): Promise<T> =>
  _request<T>("PATCH", path, body);
export const apiDelete = <T>(path: string): Promise<T> => _request<T>("DELETE", path);
